import { yupResolver } from '@hookform/resolvers/yup';
import { addYears, format, parseISO } from 'date-fns';
import * as Yup from 'yup';

import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCreateNewBorderCrossingRoroRequestAsync } from '@/features/borderCrossingRORO/createNewRequest/apis';
import {
  RegisterNewRequestForm,
  REGISTER_NEW_REQUEST_FORM,
} from '@/features/borderCrossingRORO/createNewRequest/types';
import { useGetFeatureFlagAsync } from '@/features/featuresFlag/apis';
import { FEATURES_FLAG_KEYS } from '@/features/featuresFlag/apis/featureFlag.keys';
import { CROSSING_DIRECTION_COUNTRY, LANGUAGES } from '@/features/shared/constants';
import { isBorderCrossingRoRo, OPEN_CUSTOMS_AND_CROSSING } from '@/features/shared/helpers/typologies';

export const useCreateNewRequest = () => {
  const navigate = useNavigate();
  const schema = useFormSchema();
  const [isCreateNewRequestInError, setIsCreateNewRequestInError] = useState<boolean>(false);

  const { data: isGmrEloOptionActivatedEnabled, isLoading: isGmrCustomsProFeatureFlagLoading } = useGetFeatureFlagAsync(
    {
      featureFlagKey: FEATURES_FLAG_KEYS.GMR_ELO_OPTION_ACTIVATED,
    },
  );

  const { mutate: createNewBorderCrossingRoroRequest, isLoading: isCreateNewBorderCrossingRoroRequestPending } =
    useCreateNewBorderCrossingRoroRequestAsync();

  const { control, formState, handleSubmit, register, watch } = useForm<RegisterNewRequestForm>({
    mode: 'all',
    defaultValues: {
      requiresGmrAndEloFormality: null,
      crossingRequestedDate: null,
      vehicleNationality: null,
      folderTypologyId: null,
      direction: null,
    },
    resolver: yupResolver(schema),
  });

  const handleCancelTheRequest = useCallback(() => {
    navigate('/service-offers');
  }, [navigate]);

  const handleSubmitNewRequest = handleSubmit((data: RegisterNewRequestForm) => {
    const {
      direction,
      tractorVrn,
      trailerVrn,
      folderTypologyId,
      vehicleNationality,
      haulierEuEoriNumber,
      haulierGbEoriNumber,
      crossingRequestedDate,
      internalClientReference,
      requiresGmrAndEloFormality,
    } = data;

    createNewBorderCrossingRoroRequest(
      {
        data: {
          direction,
          tractorVrn,
          trailerVrn,
          vehicleNationality,
          internalClientReference,
          crossingRequestedDate: format(new Date(crossingRequestedDate), 'yyyy-MM-dd'),
          requiresGmrFormality:
            folderTypologyId === OPEN_CUSTOMS_AND_CROSSING.BORDER_CROSSING_RORO
              ? JSON.parse(requiresGmrAndEloFormality)
              : undefined,
          requiresGmrAndEloFormality:
            folderTypologyId === OPEN_CUSTOMS_AND_CROSSING.BORDER_CROSSING_RORO_EMPTY_V2
              ? JSON.parse(requiresGmrAndEloFormality)
              : undefined,
        },
        folderTypologyId,
      },
      {
        onSuccess: ({ data: folderId }) => {
          setIsCreateNewRequestInError(false);
          const isEuToUk = direction === CROSSING_DIRECTION_COUNTRY.EUUK;

          const paramKey = isEuToUk ? 'haulierGbEoriNumber' : 'haulierEuEoriNumber';

          const paramValue = isEuToUk ? haulierGbEoriNumber : haulierEuEoriNumber;

          navigate(
            `/typologies/${folderTypologyId}/folders/${folderId}/applicant-transmitted-documents?${paramKey}=${paramValue
              ?.trim()
              .replace(/\s/g, '')}`,
          );
        },
        onError: () => {
          setIsCreateNewRequestInError(true);
        },
      },
    );
  });

  const handleCloseToaster = useCallback(() => {
    setIsCreateNewRequestInError(false);
  }, []);

  return {
    isCreateNewBorderCrossingRoroRequestPending,
    isGmrCustomsProFeatureFlagLoading,
    isGmrEloOptionActivatedEnabled,
    isCreateNewRequestInError,
    formState,
    control,
    watch,
    register,
    handleCloseToaster,
    handleCancelTheRequest,
    handleSubmitNewRequest,
  };
};

function useFormSchema() {
  const {
    t,
    i18n: { language },
  } = useTranslation('borderCrossingRORO_newRequest');

  const todayDate = new Date().toISOString().split('T')[0];
  const oneYearLater = addYears(new Date(), 1).toISOString().split('T')[0];

  return Yup.object().shape({
    internalClientReference: Yup.string()
      .trim()
      .matches(/^[a-zA-Z0-9 -]*$/, t('borderCrossingRORO.newRequest.form.alphanumeric.pattern.errorMessage'))
      .max(60, t('borderCrossingRORO.newRequest.form.maxLength.error.message', { maxLength: 60 })),
    direction: Yup.string().nullable().required(t('borderCrossingRORO.newRequest.form.fieldIsMandatory.errorMessage')),
    trailerVrn: Yup.string()
      .trim()
      .uppercase()
      .required(t('borderCrossingRORO.newRequest.form.fieldIsMandatory.errorMessage'))
      .matches(/^[a-zA-Z0-9]+$/, t('borderCrossingRORO.newRequest.form.alphanumeric.pattern.errorMessage'))
      .max(
        10,
        t('borderCrossingRORO.newRequest.form.maxLength.error.message', {
          maxLength: 10,
        }),
      )
      .transform((value: string) => value?.replace(/\s+/g, '').replace('-', '')),
    tractorVrn: Yup.string()
      .trim()
      .uppercase()
      .required(t('borderCrossingRORO.newRequest.form.fieldIsMandatory.errorMessage'))
      .matches(/^[a-zA-Z0-9]+$/, t('borderCrossingRORO.newRequest.form.alphanumeric.pattern.errorMessage'))
      .max(
        10,
        t('borderCrossingRORO.newRequest.form.maxLength.error.message', {
          maxLength: 10,
        }),
      )
      .transform((value: string) => value?.replace(/\s+/g, '').replace('-', '')),
    requiresGmrAndEloFormality: Yup.boolean()
      .nullable()
      .required(t('borderCrossingRORO.newRequest.form.fieldIsMandatory.errorMessage'))
      .transform((value) => Boolean(value)),
    folderTypologyId: Yup.string()
      .nullable()
      .required(t('borderCrossingRORO.newRequest.form.fieldIsMandatory.errorMessage')),
    crossingRequestedDate: Yup.date()
      .nullable()
      .typeError(t('borderCrossingRORO.newRequest.form.valideType.errorMessage'))
      .required(t('borderCrossingRORO.newRequest.form.fieldIsMandatory.errorMessage'))
      .min(
        todayDate,
        t('borderCrossingRORO.newRequest.form.date.min.errorMessage', {
          min: format(parseISO(todayDate), language === LANGUAGES.fr ? 'dd-MM-yyyy' : 'yyyy-MM-dd'),
        }),
      )
      .max(
        oneYearLater,
        t('borderCrossingRORO.newRequest.form.date.max.errorMessage', {
          max: format(parseISO(oneYearLater), language === LANGUAGES.fr ? 'dd-MM-yyyy' : 'yyyy-MM-dd'),
        }),
      ),
    vehicleNationality: Yup.string()
      .nullable()
      .when(REGISTER_NEW_REQUEST_FORM.folderTypologyId, {
        is: (folderTypologyId: string) => isBorderCrossingRoRo({ folderTypologyId }),
        then: (schema) => schema.required(t('borderCrossingRORO.newRequest.form.fieldIsMandatory.errorMessage')),
        otherwise: (schema) => schema.notRequired(),
      }),
    haulierGbEoriNumber: Yup.string().when(
      [REGISTER_NEW_REQUEST_FORM.folderTypologyId, REGISTER_NEW_REQUEST_FORM.direction],
      {
        is: (folderTypologyId: string, direction: string) =>
          isBorderCrossingRoRo({ folderTypologyId }) && direction === CROSSING_DIRECTION_COUNTRY.EUUK,
        then: (schema) =>
          schema
            .required(t('borderCrossingRORO.newRequest.form.fieldIsMandatory.errorMessage'))
            .matches(
              /^GB\d{12}$/,
              t('borderCrossingRORO.newRequest.form.haulierGbEoriNumber.regex.error.message', {
                prefix: 'GB',
                lengthOfDigit: 12,
              }),
            )
            .transform((value: string) => value?.replace(/\s+/g, '')),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
    haulierEuEoriNumber: Yup.string().when(
      [REGISTER_NEW_REQUEST_FORM.folderTypologyId, REGISTER_NEW_REQUEST_FORM.direction],
      {
        is: (folderTypologyId: string, direction: string) =>
          isBorderCrossingRoRo({ folderTypologyId }) && direction === CROSSING_DIRECTION_COUNTRY.UKEU,
        then: (schema) =>
          schema
            .required(t('borderCrossingRORO.newRequest.form.fieldIsMandatory.errorMessage'))
            .matches(
              /^(?!GB)[A-Z]{2}[A-Z0-9]{6,18}$/,
              t('borderCrossingRORO.newRequest.form.haulierEuEoriNumber.regex.error.message', {
                minOfLength: 8,
                maxOfLength: 20,
              }),
            )
            .transform((value: string) => value?.replace(/\s+/g, '')),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
  });
}
